import { Injectable } from '@angular/core';

import {
  AssistantContextReviewRequest,
  AssistantContextReviewResponse,
  AssistantContextSettings,
  AssistantProviderId,
  AssistantProvidersResponse
} from '@core/models/assistant/assistant-settings.model';
import {
  AssistantChatRequest,
  AssistantConfig,
  AssistantErrorCode,
  AssistantEvent
} from '@core/models/assistant/assistant.model';
import { convertKeysToSnakeCase } from '@core/utils/case-conversions';
import { readSseFrames } from '@core/utils/sse.utils';

const BASE_URL = '/assistant';

export class AssistantConfigError extends Error {
  constructor(readonly code: AssistantErrorCode) {
    super(`assistant config failed: ${code}`);
  }
}

/**
 * A context review the server did not answer with a verdict. `provider` names the provider whose key the
 * review needs; `source` says whose key the provider refused (`user` is the one the admin typed).
 */
export class AssistantReviewError extends Error {
  constructor(
    readonly code: AssistantErrorCode | 'review_key_required' | 'no_provider',
    readonly provider?: AssistantProviderId,
    readonly source?: string
  ) {
    super(`assistant context review failed: ${code}`);
  }
}

const CONTEXT_FIELDS: Record<string, keyof AssistantContextSettings> = {
  use_cases: 'useCases',
  vocabulary: 'vocabulary',
  answer_style: 'answerStyle',
  additional: 'additional'
};

const CONFIG_STATUS_CODES: Record<number, AssistantErrorCode> = {
  401: 'unauthenticated',
  403: 'forbidden'
};

// fetch rather than HttpClient: the stream must be read incrementally, and /assistant sits outside
// GBE_BASE_URL, so the GBE interceptors (auth, case conversion) must not apply.
@Injectable({ providedIn: 'root' })
export class AssistantApiService {
  async getConfig(dataSpaceId: number, token: string): Promise<AssistantConfig> {
    const res = await fetch(`${BASE_URL}/config?data_space_id=${dataSpaceId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    if (!res.ok)
      throw new AssistantConfigError(CONFIG_STATUS_CODES[res.status] ?? 'internal');
    return (await res.json()) as AssistantConfig;
  }

  async getProviders(token: string): Promise<AssistantProvidersResponse> {
    const res = await fetch(`${BASE_URL}/providers`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    if (!res.ok)
      throw new AssistantConfigError(CONFIG_STATUS_CODES[res.status] ?? 'internal');
    return (await res.json()) as AssistantProvidersResponse;
  }

  async reviewContext(
    body: AssistantContextReviewRequest,
    token: string
  ): Promise<AssistantContextReviewResponse> {
    const { context: c } = body;
    const res = await fetch(`${BASE_URL}/context/review`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({
        data_space_id: body.dataSpaceId,
        lang: body.lang,
        context: {
          use_cases: c.useCases,
          vocabulary: c.vocabulary,
          answer_style: c.answerStyle,
          additional: c.additional
        },
        ...(body.apiKey !== undefined ? { api_key: body.apiKey } : {}),
        ...(body.providers !== undefined
          ? { providers: convertKeysToSnakeCase(body.providers) }
          : {})
      })
    });
    // A body is present only when the assistant server itself answered; a proxy error page has none.
    const answer = (await res.json().catch(() => null)) as Record<string, unknown> | null;
    if (!res.ok) {
      throw new AssistantReviewError(
        (answer?.['code'] as AssistantReviewError['code']) ?? 'internal',
        answer?.['provider'] as AssistantProviderId | undefined,
        answer?.['source'] as string | undefined
      );
    }
    if (answer?.['approved'] === true) {
      const review = answer['context_review'] as {
        rules_version: number;
        stamp: string;
      } | null;
      return {
        approved: true,
        contextReview: review && { rulesVersion: review.rules_version, stamp: review.stamp }
      };
    }
    if (answer?.['approved'] !== false || !Array.isArray(answer['findings']))
      throw new AssistantReviewError('internal');
    const findings = answer['findings'] as { field: string; reason: string }[];
    return {
      approved: false,
      findings: findings.flatMap(({ field, reason }) =>
        Object.hasOwn(CONTEXT_FIELDS, field)
          ? [{ field: CONTEXT_FIELDS[field], reason }]
          : []
      )
    };
  }

  async endConversation(conversationId: string, token: string): Promise<void> {
    await fetch(`${BASE_URL}/conversations/${encodeURIComponent(conversationId)}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  /** `gone`: the server no longer knows the session, so nothing holds the plan through it. */
  async closePlanEdit(
    conversationId: string,
    editSessionId: string,
    token: string
  ): Promise<'closed' | 'gone' | 'failed'> {
    const res = await fetch(
      `${BASE_URL}/conversations/${encodeURIComponent(conversationId)}/plan-edits/${encodeURIComponent(editSessionId)}/close`,
      { method: 'POST', headers: { Authorization: `Bearer ${token}` } }
    ).catch(() => null);
    if (res?.status === 204) return 'closed';
    if (res?.status === 404) return 'gone';
    return 'failed';
  }

  async *chat(
    body: AssistantChatRequest,
    token: string,
    signal: AbortSignal
  ): AsyncGenerator<AssistantEvent> {
    const res = await fetch(`${BASE_URL}/chat`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        Accept: 'text/event-stream'
      },
      body: JSON.stringify(body),
      signal
    });
    if (!res.ok || !res.body) {
      // A body is present only when the assistant server itself answered; a proxy error page has none.
      const detail = (await res.json().catch(() => null)) as {
        code?: AssistantErrorCode;
        message?: string;
        retryable?: boolean;
      } | null;
      yield {
        event: 'error',
        data: {
          code: detail?.code ?? (res.status === 400 ? 'bad_request' : 'internal'),
          message: detail?.message ?? `HTTP ${res.status}`,
          retryable: detail?.retryable ?? res.status >= 500,
          // Refused before a turn started, so no conversation was touched.
          conversation_discarded: false
        }
      };
      return;
    }
    for await (const frame of readSseFrames(res.body)) {
      yield { event: frame.event, data: JSON.parse(frame.data) } as AssistantEvent;
    }
  }
}
